import { Modal } from 'stories/components'
import { formatDate } from 'utils'

/**
 * Explains how the Principal & Interest was calculated. Non-Dutch loans use the
 * daily balance over the accrual month (each dated draw only accruing from its
 * day) times a true daily rate; Dutch loans accrue interest on the full committed
 * principal balance at the note rate. `breakdown` is the object the backend
 * returns alongside the P&I (see computeNonDutchDailyBalancePI / the Dutch branch
 * of getLoanPaymentFillData); `breakdown.mode === 'dutch'` selects the Dutch view.
 *
 * A Non-Dutch loan that is not interest only (`breakdown.interestOnly === false`)
 * pays the level payment that amortizes its balance over the term left to maturity;
 * the daily-balance interest above is that payment's interest component.
 */
const money = (v: number) =>
  (Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const PaymentInterestCalcModal = (props: { breakdown: any; rate?: any; onClose: () => void }) => {
  const b = props.breakdown || {}
  const segments: any[] = b.segments || []
  const draws: any[] = b.draws || []
  // Only the Non-Dutch view carries the flag; `undefined` (Dutch / older payloads) reads as interest only.
  const amortizing = b.interestOnly === false

  const row = (label: string, value: string) => (
    <div className="flex justify-between gap-6 py-0.5">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium whitespace-nowrap">{value}</span>
    </div>
  )

  // The unpaid balances this loan accrues interest on beyond principal, one row per kind ticked on
  // Interest Calculations. Older payloads carry only the total, which was always unpaid interest.
  const ib = b.interestBearing
  const bearingRows: [string, number][] = ib
    ? ([
        ['Unpaid Interest', ib.unpaidInterest],
        ['Unpaid Charges', ib.unpaidCharges],
        ['Unpaid Late Charges', ib.unpaidLateCharges],
      ] as [string, number][])
    : [['Unpaid Interest', b.compoundedUnpaidInterest]]
  const bearingLines = (prefix: string) =>
    bearingRows
      .filter(([, amount]) => Number(amount) > 0)
      .map(([label, amount]) => <div key={label}>{row(`${prefix}${label}`, `$${money(amount)}`)}</div>)

  if (b.mode === 'dutch') {
    const rate = b.rate ?? props.rate ?? ''
    const hasParticipation = Number(b.participationInterest) > 0 || Number(b.participationFundingTotal) > 0
    // A month of interest on the unpaid balances accruing interest rides on the payment.
    const hasCompound = Number(b.compoundInterest) > 0
    return (
      <Modal title="How Principal & Interest Was Calculated" isOpen onClose={props.onClose} titleOkay="" titleCancel="Close">
        <div className="w-full max-w-[600px] text-sm">
          <p className="mb-3 text-gray-600">
            Dutch interest accrues on the full committed principal balance at the note rate, so the monthly P&amp;I is
            the interest on that balance.
          </p>

          <div className="mb-4 rounded bg-gray-50 p-3">
            {row('Principal Balance', `$${money(b.principalBalance)}`)}
            {row('Interest Rate', `${rate}%`)}
            {row('Scheduled P&I', `$${money(b.scheduledPI)}`)}
            {hasParticipation && row('Participation Draws', `$${money(b.participationFundingTotal)}`)}
            {hasParticipation && row('Interest on Participation Draws', `+$${money(b.participationInterest)}`)}
            {hasCompound && bearingLines('Accruing Interest: ')}
            {hasCompound && row('Interest on Unpaid Balances', `+$${money(b.compoundInterest)}`)}
          </div>

          <div className="mt-4 border-t border-gray-300 pt-3">
            <p className="mb-1 font-semibold">Formula</p>
            <p className="mb-2 text-gray-600">Interest = Principal Balance × Rate ÷ (12 × 100)</p>
            {row(`($${money(b.principalBalance)} × ${rate}) ÷ (12 × 100)`, `$${money(b.scheduledPI)}`)}
            {hasParticipation && row('+ Interest on Participation Draws', `$${money(b.participationInterest)}`)}
            {hasCompound && row('+ Interest on Unpaid Balances', `$${money(b.compoundInterest)}`)}
            {row('Principal & Interest', `$${money(b.amount)}`)}
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title="How Principal & Interest Was Calculated" isOpen onClose={props.onClose} titleOkay="" titleCancel="Close">
      <div className="w-full max-w-[600px] text-sm">
        <p className="mb-3 text-gray-600">
          Non-Dutch interest for the accrual period, charged on the daily balance so each draw only accrues interest
          from the day it posts.
          {amortizing
            ? ' This loan is not interest only, so the payment is the level payment that amortizes the balance over the term left to maturity.'
            : ''}
        </p>

        <div className="mb-4 rounded bg-gray-50 p-3">
          {row('Accrual Period', `${formatDate(b.monthStart)} – ${formatDate(b.monthEnd)} (${b.daysInMonth} days)`)}
          {row('Interest Rate', `${b.rate ?? props.rate ?? ''}%`)}
          {row('Daily Rate Basis', `Actual/${b.denom}`)}
          {row('Opening Balance', `$${money(b.openingBalance)}`)}
          {Number(b.compoundedUnpaidInterest) > 0 && bearingLines('Includes ')}
        </div>

        {draws.length > 0 && (
          <div className="mb-4">
            <p className="mb-1 font-semibold">Draws applied this period</p>
            <table className="w-full">
              <tbody>
                {draws.map((d, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-1">{formatDate(d.date)}</td>
                    <td className="py-1 text-right font-medium">+${money(d.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="mb-1 font-semibold">Daily balance</p>
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="py-1 font-medium">Period</th>
              <th className="py-1 text-right font-medium">Balance</th>
              <th className="py-1 text-right font-medium">Days</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((s, i) => (
              <tr key={i} className="border-b">
                <td className="py-1">
                  {s.fromDay === s.toDay ? `Day ${s.fromDay}` : `Days ${s.fromDay}–${s.toDay}`}
                </td>
                <td className="py-1 text-right">${money(s.balance)}</td>
                <td className="py-1 text-right">{s.days}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 border-t border-gray-300 pt-3">
          <p className="mb-1 font-semibold">Formula</p>
          <p className="mb-2 text-gray-600">Interest = Σ(Balance × Days) × Rate ÷ (Days Basis × 100)</p>
          {row(
            `(${money(b.sumDailyBalances)} × ${b.rate}) ÷ (${b.denom} × 100)`,
            `$${money(amortizing ? b.interestPortion : b.amount)}`,
          )}
          {amortizing && (
            <>
              <p className="mt-3 mb-1 font-semibold">Amortized payment</p>
              <p className="mb-2 text-gray-600">
                Payment = Balance × r ÷ (1 − (1 + r)<sup>−n</sup>), where r is the monthly rate and n the payments left
                to maturity.
              </p>
              {row('Balance (end of period)', `$${money(b.closingBalance)}`)}
              {row('Payments Remaining (n)', `${b.remainingTerm ?? ''}`)}
              {row('Monthly Rate (r)', `${((Number(b.rate) || 0) / 12).toFixed(4)}%`)}
              {row('Interest Portion', `$${money(b.interestPortion)}`)}
              {row('Principal Portion', `$${money(b.principalPortion)}`)}
            </>
          )}
          {row('Principal & Interest', `$${money(b.amount)}`)}
        </div>
      </div>
    </Modal>
  )
}
